import React from 'react';

interface FloralDecorationProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  'aria-hidden'?: boolean;
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
};

const positionClasses = {
  'top-left': 'top-4 left-4',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-right': 'bottom-4 right-4',
};

export const FloralCornerDecoration: React.FC<FloralDecorationProps> = ({
  className = '',
  size = 'md',
  position = 'top-left',
  'aria-hidden': ariaHidden = true,
}) => {
  return (
    <div
      className={`absolute ${positionClasses[position]} ${sizeClasses[size]} ${className}`}
      aria-hidden={ariaHidden}
    >
      <svg
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full opacity-60"
        style={{ zIndex: -1, pointerEvents: 'none' }}
      >
        <g>
          {/* Interlocked Wedding Rings - Larger and more prominent */}
          <circle
            cx="35"
            cy="40"
            r="18"
            fill="none"
            stroke="#8FA68E"
            strokeWidth="5"
            opacity="0.9"
          />
          <circle
            cx="50"
            cy="55"
            r="18"
            fill="none"
            stroke="#6B8E6B"
            strokeWidth="5"
            opacity="0.8"
          />
          {/* Ring highlights for 3D effect */}
          <circle
            cx="35"
            cy="40"
            r="18"
            fill="none"
            stroke="#B8D4C7"
            strokeWidth="2"
            strokeDasharray="8 12"
            opacity="0.6"
          />
          <circle
            cx="50"
            cy="55"
            r="18"
            fill="none"
            stroke="#A8C4A8"
            strokeWidth="2"
            strokeDasharray="8 12"
            opacity="0.5"
          />
          {/* Small decorative sparkles */}
          <circle cx="20" cy="25" r="1.5" fill="#8FA68E" opacity="0.8" />
          <circle cx="65" cy="40" r="1.5" fill="#8FA68E" opacity="0.8" />
          <circle cx="25" cy="70" r="1.5" fill="#8FA68E" opacity="0.8" />
          <circle cx="70" cy="70" r="1.5" fill="#8FA68E" opacity="0.8" />
          {/* Delicate flourishes */}
          <path
            d="M15 20L20 25M70 35L75 40M20 75L25 70M75 75L70 70"
            stroke="#B8D4C7"
            strokeWidth="1"
            opacity="0.6"
          />
        </g>
      </svg>
    </div>
  );
};

export const FloralDivider: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`flex items-center justify-center my-6 ${className}`} role="separator">
      <svg
        width="200"
        height="40"
        viewBox="0 0 200 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Central flower */}
        <g transform="translate(100, 20)">
          <circle cx="0" cy="0" r="3" fill="#8FA68E" opacity="0.8" />
          <path d="M-6 0C-6 -3 -3 -6 0 -6C3 -6 6 -3 6 0C6 3 3 6 0 6C-3 6 -6 3 -6 0Z" fill="#B8D4C7" opacity="0.7" />
          <path d="M0 -8L-2 -4L2 -4ZM8 0L4 -2L4 2ZM0 8L2 4L-2 4ZM-8 0L-4 2L-4 -2Z" fill="#8FA68E" opacity="0.6" />
        </g>
        
        {/* Flowing vine on left */}
        <path
          d="M20 20Q40 15 60 20Q80 25 100 20"
          stroke="#8FA68E"
          strokeWidth="1.5"
          fill="none"
          opacity="0.7"
        />
        
        {/* Flowing vine on right */}
        <path
          d="M100 20Q120 25 140 20Q160 15 180 20"
          stroke="#8FA68E"
          strokeWidth="1.5"
          fill="none"
          opacity="0.7"
        />
        
        {/* Small leaves along the vines */}
        <ellipse cx="40" cy="17" rx="3" ry="1.5" fill="#B8D4C7" opacity="0.6" transform="rotate(-20 40 17)" />
        <ellipse cx="80" cy="23" rx="3" ry="1.5" fill="#B8D4C7" opacity="0.6" transform="rotate(20 80 23)" />
        <ellipse cx="120" cy="23" rx="3" ry="1.5" fill="#B8D4C7" opacity="0.6" transform="rotate(-20 120 23)" />
        <ellipse cx="160" cy="17" rx="3" ry="1.5" fill="#B8D4C7" opacity="0.6" transform="rotate(20 160 17)" />
        
        {/* Small buds */}
        <circle cx="30" cy="18" r="1.5" fill="#8FA68E" opacity="0.8" />
        <circle cx="70" cy="22" r="1.5" fill="#8FA68E" opacity="0.8" />
        <circle cx="130" cy="22" r="1.5" fill="#8FA68E" opacity="0.8" />
        <circle cx="170" cy="18" r="1.5" fill="#8FA68E" opacity="0.8" />
      </svg>
    </div>
  );
};

export const WatercolorBackground: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`absolute inset-0 ${className}`} aria-hidden="true" style={{ zIndex: -1, pointerEvents: 'none' }}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 800 600"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <filter id="blur1" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
          </filter>
          <filter id="blur2" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="12" />
          </filter>
        </defs>
        
        <ellipse cx="150" cy="120" rx="80" ry="60" fill="#E8F4F8" opacity="0.6" filter="url(#blur1)" />
        <ellipse cx="650" cy="180" rx="100" ry="70" fill="#B8D4C7" opacity="0.4" filter="url(#blur2)" />
        <ellipse cx="300" cy="400" rx="90" ry="65" fill="#E8F4F8" opacity="0.5" filter="url(#blur1)" />
        <ellipse cx="600" cy="450" rx="70" ry="50" fill="#8FA68E" opacity="0.3" filter="url(#blur2)" />
        <ellipse cx="100" cy="350" rx="60" ry="45" fill="#B8D4C7" opacity="0.4" filter="url(#blur1)" />
        
        <circle cx="200" cy="200" r="3" fill="#8FA68E" opacity="0.6" />
        <circle cx="500" cy="150" r="2" fill="#B8D4C7" opacity="0.7" />
        <circle cx="400" cy="300" r="2.5" fill="#8FA68E" opacity="0.5" />
        <circle cx="700" cy="350" r="2" fill="#B8D4C7" opacity="0.6" />
        <circle cx="150" cy="450" r="3" fill="#8FA68E" opacity="0.4" />
      </svg>
    </div>
  );
};

export const FloralHeader: React.FC<{
  bride: string;
  groom: string;
  date: string;
  className?: string;
  selectedLanguage?: 'english' | 'iban';
}> = ({ bride, groom, date, className = '', selectedLanguage = 'english' }) => {
  return (
    <div className={`text-center space-y-4 ${className}`}>
      <div className="space-y-2">
        {selectedLanguage === 'iban' ? (
          <>
            <p className="text-sage-600 text-sm font-medium tracking-wider uppercase">
              Enggau ati ti gaga, kami ngelaluka nuan
            </p>
            <p className="text-sage-600 text-sm font-medium tracking-wider uppercase">
              ngagai pengerami tikah
            </p>
          </>
        ) : (
          <>
            <p className="text-sage-600 text-sm font-medium tracking-wider uppercase">
              With hearts full of joy, you are invited
            </p>
            <p className="text-sage-600 text-sm font-medium tracking-wider uppercase">
              to the wedding of
            </p>
          </>
        )}
      </div>
      
      <div className="space-y-1">
        <h1 className="floral-title text-4xl md:text-5xl font-serif italic text-sage-800">
          {bride}
        </h1>
        <div className="text-3xl md:text-4xl font-serif italic text-sage-700">&</div>
        <h1 className="floral-title text-4xl md:text-5xl font-serif italic text-sage-800">
          {groom}
        </h1>
      </div>
      
      <div className="space-y-1 pt-4">
        <p className="text-lg font-serif text-sage-700">{date}</p>
      </div>
    </div>
  );
};